import styled from '@emotion/styled';
import { withConfig } from 'src/withConfig';
import { InfoIcon } from 'src/domains/layouts/shared/assets/iconography/interface/InfoIcon';
import { css, SerializedStyles } from '@emotion/react';
import { EmotionTheme } from 'src/domains/layouts/config/emotionTheme';
import { TooltipType } from 'src/domains/players/webview/components/Account/summaryTab/accountSummary/Tooltip/Tooltip';

const buildTooltipIconStyles = (theme: EmotionTheme, type: TooltipType): SerializedStyles => {
    switch (type) {
        case 'default': {
            return css`
                color: ${theme.star.tooltip.bgColor};
                fill: ${theme.star.tooltip.bgColor};
            `;
        }
        case 'info': {
            return css`
                color: ${theme.star.tooltip.bgColorSecondary};
                fill: ${theme.star.tooltip.bgColorSecondary};
            `;
        }
        case 'error': {
            return css`
                fill: ${theme.star.tooltip.bgColorTertiary};
                color: ${theme.star.tooltip.bgColorTertiary};
            `;
        }
    }
};

const buildTooltipTriangleStyles = (theme: EmotionTheme, type: TooltipType): SerializedStyles => {
    switch (type) {
        case 'default': {
            return css`
                border-top: 8px solid ${theme.star.tooltip.bgColor};
            `;
        }
        case 'info': {
            return css`
                border-top: 8px solid ${theme.star.tooltip.bgColorSecondary};
            `;
        }
        case 'error': {
            return css`
                border-top: 8px solid ${theme.star.tooltip.bgColorTertiary};
            `;
        }
    }
};

const buildTooltipContentStyles = (theme: EmotionTheme, type: TooltipType): SerializedStyles => {
    switch (type) {
        case 'default': {
            return css`
                background: ${theme.star.tooltip.bgColor};
                color: ${theme.star.tooltip.txtColor};
            `;
        }
        case 'info': {
            return css`
                background: ${theme.star.tooltip.bgColorSecondary};
                color: ${theme.star.tooltip.txtColorSecondary};
            `;
        }
        case 'error': {
            return css`
                background: ${theme.star.tooltip.bgColorTertiary};
                color: ${theme.star.tooltip.txtColorTertiary};
            `;
        }
    }
};

const buildPortalTooltipTriangleStyles = (theme: EmotionTheme, type: TooltipType): SerializedStyles => {
    switch (type) {
        case 'default': {
            return css`
                border-bottom: 8px solid ${theme.star.tooltip.bgColor};
            `;
        }
        case 'info': {
            return css`
                border-bottom: 8px solid ${theme.star.tooltip.bgColorSecondary};
            `;
        }
        case 'error': {
            return css`
                border-bottom: 8px solid ${theme.star.tooltip.bgColorTertiary};
            `;
        }
    }
};

interface TooltipPropsType {
    type: TooltipType;
}

export const TooltipContainer = styled('div', { label: 'TooltipContainer' })`
    &:hover .tooltip-content {
        visibility: visible;
    }

    &:hover .tooltip-triangle {
        visibility: visible;
    }
`;

export const IconContainer = styled('div', { label: 'IconContainer' })`
    display: flex;
    align-items: center;
`;

export const Icon = withConfig(theme => styled(InfoIcon, { label: 'Icon' })<TooltipPropsType>`
    height: 16px;
    width: 16px;

    ${({ type }): SerializedStyles => buildTooltipIconStyles(theme, type)};
`);

export const TooltipTriangle = withConfig(theme => styled('div', { label: 'TooltipTriangle' })<TooltipPropsType>`
    position: absolute;
    bottom: 100%;
    width: 0;
    height: 0;
    margin-left: 4px;
    margin-bottom: 8px;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    z-index: ${theme.star.zIndexLevelBase.baseUp};
    visibility: hidden;

    ${({ type }): SerializedStyles => buildTooltipTriangleStyles(theme, type)};
`);

export const TooltipContent = withConfig(theme => styled('div', { label: 'TooltipContent' })<TooltipPropsType>`
    position: absolute;
    bottom: 100%;
    margin-bottom: 16px;
    width: 100%;
    left: 0;
    font-size: ${theme.star.fontSize.xSmall};
    font-weight: ${theme.star.fontWeight.xMedium};
    line-height: 1.2;
    padding: 8px;
    z-index: ${theme.star.zIndexLevelBase.baseUp};
    visibility: hidden;
    border-radius: 4px;
    text-align: center;
    box-shadow: 0 4px 8px 3px rgba(0, 0, 0, 0.15), 0 1px 3px 0 rgba(0, 0, 0, 0.15);

    ${({ type }): SerializedStyles => buildTooltipContentStyles(theme, type)};
`);

/** Portal mode — used when tooltip must escape overflow / overlays. */

export const PortalTooltipContainer = styled('span', { label: 'PortalTooltipContainer' })`
    display: inline-flex;
    flex-shrink: 0;
    position: relative;
    vertical-align: middle;
`;

export const PortalTooltipTrigger = styled('button', { label: 'PortalTooltipTrigger' })`
    align-items: center;
    appearance: none;
    background: none;
    border: none;
    color: inherit;
    cursor: pointer;
    display: inline-flex;
    height: 16px;
    opacity: 0.45;
    padding: 0;
    width: 16px;

    &:hover,
    &:focus,
    &:focus-visible {
        opacity: 0.85;
    }
`;

export const PortalTooltipLayer = withConfig(theme => styled('div', { label: 'PortalTooltipLayer' })`
    pointer-events: none;
    position: fixed;
    z-index: ${theme.star.zIndexGlobal.highLevel + 1};
`);

export const PortalTooltipTriangle = withConfig(theme => styled('span', { label: 'PortalTooltipTriangle' })<TooltipPropsType>`
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    height: 0;
    margin-left: -8px;
    position: absolute;
    top: -8px;
    width: 0;

    ${({ type }): SerializedStyles => buildPortalTooltipTriangleStyles(theme, type)};
`);

export const PortalTooltipContent = withConfig(theme => styled('span', { label: 'PortalTooltipContent' })<TooltipPropsType>`
    border-radius: 4px;
    box-shadow: 0 4px 8px 3px rgba(0, 0, 0, 0.15), 0 1px 3px 0 rgba(0, 0, 0, 0.15);
    box-sizing: border-box;
    display: block;
    font-size: ${theme.star.fontSize.xSmall};
    font-weight: ${theme.star.fontWeight.xMedium};
    line-height: 1.3;
    padding: 8px;
    text-align: left;
    white-space: normal;

    ${({ type }): SerializedStyles => buildTooltipContentStyles(theme, type)};
`);
